import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {List, Map, Set} from 'immutable';
import {KpiObservation, KpiTarget, KpiTargetId, KpiChartOption} from '@store/@types/kpi';
import MultiSelect from '@components/multi-select';
import CoachingPlan from 'records/CoachingPlan';
import {CoachingPlanVelocity} from 'records/CoachingPlanVelocity';
import moment, {Moment} from 'moment';
import User, {UserId} from 'records/User';
import KpiGoalActions from '@store/kpi/KpiTargetActions';
import {
  CoachingGoalsChart,
  CoachingGoalsChartDataItem,
} from 'components/coaching/coaching-goals-view/CoachingGoalsChart';
import CoachingPlanOverviewSkillOption from 'components/coaching/coaching-goals-view/CoachingPlanOverviewSkillOption';

export interface CoachingGoalsViewProps {
  getChartOptions: (plan: CoachingPlan) => KpiChartOption[];
  observations: List<KpiObservation>;
  plan: CoachingPlan;
  velocity: CoachingPlanVelocity;
  users: Map<UserId, User>;
  kpisById: Map<KpiTargetId, KpiTarget>;
  fetchKpis: typeof KpiGoalActions.fetchGoalKpiTree;
}

export const CoachingGoalsView: React.FC<CoachingGoalsViewProps> = ({
  getChartOptions,
  observations,
  plan,
  velocity,
  users,
  kpisById,
  fetchKpis,
}) => {
  const [selectedGoalIds, setSelectedGoalIds] = useState<Array<KpiTargetId> | null>(null);
  useEffect(() => {
    fetchKpis.defer();
  }, [fetchKpis]);

  const options = useMemo(() => {
    return getChartOptions(plan);
  }, [getChartOptions, plan]);

  // Selects default goals to show
  useEffect(() => {
    if (selectedGoalIds !== null) return;
    if (options.length === 0) return;

    // Check if we have any skill related kpis
    const relatedKpis = Set(options.filter(o => o.group !== 'Unrelated').map(o => o.kpiId));
    // Favor skill related observations for preselection, if they're available.
    const validObservations = relatedKpis.isEmpty()
      ? observations
      : observations.filter(o => relatedKpis.has(o.kpiId));

    const kpiIds: KpiTargetId[] = validObservations
      // Check how many entries each kpi has
      .groupBy(o => o.kpiId)
      .map(val => val.size)
      .sort()
      // take the two with the most
      .take(2)
      .keySeq()
      .toArray();
    // and automatically select them.
    setSelectedGoalIds(kpiIds);
  }, [observations, selectedGoalIds, options]);

  const data = useMemo<Array<CoachingGoalsChartDataItem>>(() => {
    if (!selectedGoalIds) {
      return [];
    }
    const obsById = observations.groupBy(o => o.kpiId);

    return selectedGoalIds.map(id => {
      const observations = obsById.get(id, List());
      const option = options.find(g => g.kpiId === id)!;
      const kpi = kpisById.get(id)!;
      const data = observations
        .map(o => ({
          date: o.created.toDate(),
          value: o.value,
          extra: {
            observedBy: users.get(o.observedByUserId),
          },
        }))
        .toArray();
      return {
        numericType: kpi.numericType,
        color: option.color,
        metricId: id,
        metricName: kpi.name,
        dataPoints: data,
        goal: kpi.value ?? 0,
      };
    });
  }, [options, observations, selectedGoalIds, users, kpisById]);

  const renderOption = useCallback(option => {
    return <CoachingPlanOverviewSkillOption {...option} />;
  }, []);

  const getOptionDisabled = useCallback(
    (option: KpiChartOption) => {
      if (!selectedGoalIds) return false;

      const selectedNumericTypes = Set(
        options.filter(g => selectedGoalIds.includes(g.kpiId)).map(g => g.numericType)
      );

      // We only allow 2 numeric types on the y axis.
      // If theres already 2, then only allow the same kinds to be added.
      // Otherwise the user will need to uncheck one of the existing ones.
      return selectedNumericTypes.size === 2 && !selectedNumericTypes.has(option.numericType);
    },
    [options, selectedGoalIds]
  );

  // If theres only one option, we just render that, not the dropdown
  if (options.length === 1) {
    return (
      <>
        <CoachingGoalsChart
          from={plan.created.clone().subtract(2, 'week').toDate()}
          to={getPlanChartEnd(velocity, plan).toDate()}
          data={data}
          planStart={plan.created.toDate()}
        />
        <div className="coaching-plan-overview-multi-select-wrap">
          <CoachingPlanOverviewSkillOption {...options[0]} />
        </div>
      </>
    );
  }

  const showGroups = Set(options.map(opt => opt.group)).size > 1;
  return (
    <>
      <CoachingGoalsChart
        from={plan.created.clone().subtract(2, 'week').toDate()}
        to={getPlanChartEnd(velocity, plan).toDate()}
        data={data}
        planStart={plan.created.toDate()}
      />
      <div className="coaching-plan-overview-multi-select-wrap">
        <MultiSelect<KpiChartOption, KpiTargetId>
          placeholder={selectedGoalIds?.length === 0 ? 'Click to select goal/s' : ''}
          getValue={o => o.kpiId}
          onChange={setSelectedGoalIds}
          value={selectedGoalIds || []}
          options={options}
          renderOption={renderOption}
          getOptionDisabled={getOptionDisabled}
          groupBy={showGroups ? o => o.group : undefined}
          getLabel={o => o.name}
        />
      </div>
    </>
  );
};

/**
 * Return a reasonable end date for coaching plan graphs.
 */
export function getPlanChartEnd(velocity: CoachingPlanVelocity, plan: CoachingPlan): Moment {
  const projectedEnd = velocity.planProgress?.projectedEnd;
  if (projectedEnd) {
    return projectedEnd.clone().add(1, 'days');
  }

  const effectiveEnd = plan.schedule?.getEffectiveEndDate();
  if (effectiveEnd) {
    return effectiveEnd.clone().add(1, 'days');
  }

  return moment().add(90, 'days');
}
